import adminProductService from "../../../services/api/admin.product.service";
import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Eye,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  User,
  Package,
  Wallet,
  Clock,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Info,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductSell = () => {
  const [sellRequests, setSellRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    sellType: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    page: 1,
    limit: 10,
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionData, setActionData] = useState({
    reason: "",
    notes: "",
    executedPrice: "",
    executedQuantity: "",
    paymentMethod: "WALLET",
    isBankTransferConfirmed: false,
    transactionReference: "",
  });
  const [processing, setProcessing] = useState(false);
  const [pagination, setPagination] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const fetchSellRequests = async () => {
    try {
      setLoading(true);
      setIsSearching(!!debouncedSearchTerm);
      const params = { ...filters };
      if (debouncedSearchTerm) {
        params.search = debouncedSearchTerm;
      }

      const response = await adminProductService.getAllSellRequests(params);
      if (response.success) {
        setSellRequests(response.data || []);
        setPagination(response.pagination || {});
        setStatistics(response.statistics || null);
      } else {
        setError(response.error || "Failed to fetch sell requests");
      }
    } catch (err) {
      setError("Error fetching sell requests");
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchSellRequests();
  }, [filters, debouncedSearchTerm]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSort = (field) => {
    const newOrder =
      filters.sortBy === field && filters.sortOrder === "asc" ? "desc" : "asc";
    setFilters((prev) => ({ ...prev, sortBy: field, sortOrder: newOrder }));
  };

  const openModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setShowModal(true);
    setActionData({
      reason: "",
      notes: "",
      executedPrice: request?.pricePerShare?.toString() || "",
      executedQuantity: request?.requestedQuantity?.toString() || "",
      paymentMethod: "WALLET",
      isBankTransferConfirmed: false,
      transactionReference: "",
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setActionType("");
    setActionData({
      reason: "",
      notes: "",
      executedPrice: "",
      executedQuantity: "",
      paymentMethod: "WALLET",
      isBankTransferConfirmed: false,
      transactionReference: "",
    });
  };

  const calculateLiveStats = () => {
    if (!selectedRequest || actionType !== "APPROVED") return null;

    const qty = parseInt(actionData.executedQuantity) || 0;
    const price = parseFloat(actionData.executedPrice) || 0;
    const avgCost = selectedRequest.profitLossAnalysis.avgCostPerShare || 0;

    const totalSellAmount = qty * price;
    const totalCostBasis = qty * avgCost;
    const estimatedProfit = totalSellAmount - totalCostBasis;
    const profitPercentage = totalCostBasis > 0 ? (estimatedProfit / totalCostBasis) * 100 : 0;

    return {
      totalSellAmount,
      estimatedProfit,
      profitPercentage,
      profitLossStatus: estimatedProfit > 0 ? "PROFIT" : estimatedProfit < 0 ? "LOSS" : "BREAKEVEN",
      isModified: qty !== selectedRequest.requestedQuantity || price !== selectedRequest.pricePerShare
    };
  };

  const liveStats = calculateLiveStats();

  const handleAction = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      const payload = {
        decision: actionType,
        reason: actionData.reason,
        notes: actionData.notes,
      };

      if (actionType === "APPROVED") {
        payload.executedPrice = parseFloat(actionData.executedPrice);
        payload.executedQuantity = parseInt(actionData.executedQuantity);
        payload.paymentMethod = actionData.paymentMethod;
        payload.transactionReference = actionData.transactionReference;
      }

      const response = await adminProductService.updateSellRequestStatus(
        selectedRequest._id,
        payload
      );

      if (response.success) {
        await fetchSellRequests();
        closeModal();
        toast.success(`Request ${actionType.toLowerCase()} successfully`);
      } else {
        const errMsg = response.data?.error || "";
        const details = response.data?.details || {};
        if (errMsg.toLowerCase().includes("insufficient balance")) {
          toast.error(
            `Insufficient balance in admin wallet.
Current: ₹${details.currentBalance ?? "?"}
Required: ₹${details.requiredAmount ?? "?"}
Shortfall: ₹${details.shortfall ?? "?"}`,
            {
              autoClose: 7000,
              style: {
                whiteSpace: "pre-line",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#B91C1C",
              },
            }
          );
        } else {
          setError(errMsg || "Failed to update request");
        }
      }
    } catch (err) {
      setError("Error updating request");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "COMPLETED":
        return "bg-blue-100 text-blue-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "HIGH":
      case "URGENT":
        return "text-red-600";
      case "MEDIUM":
        return "text-yellow-600";
      case "LOW":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  const getProfitLossColor = (status) => {
    switch (status) {
      case "PROFIT":
        return "text-green-600 bg-green-50";
      case "LOSS":
        return "text-red-600 bg-red-50";
      case "BREAKEVEN":
        return "text-gray-600 bg-gray-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount) || amount === 0) return "N/A";
    return `₹${Number(amount).toLocaleString("en-IN")}`;
  };

  const formatPercentage = (percent) => {
    if (percent == null || isNaN(percent)) return "0%";
    const num = Number(percent);
    return `${num > 0 ? "+" : ""}${num.toFixed(2)}%`;
  };

  if (loading && sellRequests.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">
            Sell Requests Management
          </h1>
          <p className="text-gray-600 mt-1">
            Review and manage user sell requests with purchase history tracking
          </p>
        </div>

        {/* Statistics Dashboard */}
        {statistics && (
          <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-gray-500 mb-1">Total</div>
                <div className="text-xl font-bold text-gray-900">
                  {statistics.totalRequests || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-yellow-600 mb-1">Pending</div>
                <div className="text-xl font-bold text-yellow-600">
                  {statistics.pendingRequests || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-green-600 mb-1 flex items-center">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Profit
                </div>
                <div className="text-xl font-bold text-green-600">
                  {statistics.requestsWithProfit || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-red-600 mb-1 flex items-center">
                  <TrendingDown className="h-3 w-3 mr-1" />
                  Loss
                </div>
                <div className="text-xl font-bold text-red-600">
                  {statistics.requestsWithLoss || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-purple-600 mb-1">Negotiated</div>
                <div className="text-xl font-bold text-purple-600">
                  {statistics.requestsWithNegotiatedShares || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-orange-600 mb-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  High Risk
                </div>
                <div className="text-xl font-bold text-orange-600">
                  {statistics.highRiskRequests || 0}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <div className="text-xs text-green-600 mb-1">Approved</div>
                <div className="text-xl font-bold text-green-600">
                  {statistics.approvedRequests || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2 relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3" />
              <input
                type="text"
                placeholder="Search by user name, email or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10 pr-8 w-80"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {isSearching && (
                <div className="absolute right-3 top-2.5">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>

            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="COMPLETED">Completed</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <select
              value={filters.sellType}
              onChange={(e) => handleFilterChange("sellType", e.target.value)}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="MARKET">Market</option>
              <option value="LIMIT">Limit</option>
              <option value="HIGH_QUANTITY_SELL">High Quantity</option>
            </select>

            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange("limit", e.target.value)}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort("createdAt")}
                    className="flex items-center space-x-1 hover:text-gray-900"
                  >
                    <span>Date</span>
                    {filters.sortBy === "createdAt" &&
                      (filters.sortOrder === "asc" ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      ))}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort("totalAmount")}
                    className="flex items-center space-x-1 hover:text-gray-900"
                  >
                    <span>Amount</span>
                    {filters.sortBy === "totalAmount" &&
                      (filters.sortOrder === "asc" ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      ))}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  P/L Analysis
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort("status")}
                    className="flex items-center space-x-1 hover:text-gray-900"
                  >
                    <span>Status</span>
                    {filters.sortBy === "status" &&
                      (filters.sortOrder === "asc" ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      ))}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sellRequests.map((request) => {
                const profitLoss = request?.profitLossAnalysis;
                const riskIndicators = request?.riskIndicators;

                return (
                  <tr key={request._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-gray-400 mr-2" />
                        {request?.createdAt
                          ? new Date(request.createdAt).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request?.user?.name || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request?.user?.email || "N/A"}
                          </div>
                          {request?.user?.isKycVerified && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              KYC Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request?.product?.name || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request?.product?.category || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request?.status === "APPROVED" || request?.status === "COMPLETED" ? (
                              <div className="flex flex-col">
                                <span className="line-through text-[10px] opacity-70">
                                  {request?.requestedQuantity || 0} units @ {formatCurrency(request?.pricePerShare)}
                                </span>
                                <span className="text-green-600 font-semibold">
                                  {request?.adminResponse?.executedQuantity || request?.requestedQuantity} units @ {formatCurrency(request?.adminResponse?.executedPrice || request?.pricePerShare)}
                                </span>
                              </div>
                            ) : (
                              <span>
                                {request?.requestedQuantity || 0} units @ {formatCurrency(request?.pricePerShare)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Wallet className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(
                              (request?.status === "APPROVED" || request?.status === "COMPLETED")
                                ? (request?.adminResponse?.executedAmount || request?.totalAmount)
                                : request?.totalAmount
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request?.sellType || "N/A"}
                          </div>
                          {(riskIndicators?.hasNegotiatedShares || request?.adminResponse?.priceModified || request?.adminResponse?.quantityModified) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 mt-1 border border-purple-200">
                              Negotiated
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {profitLoss ? (
                        <div>
                          <div
                            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getProfitLossColor(
                              profitLoss.profitLossStatus
                            )}`}
                          >
                            {profitLoss.profitLossStatus === "PROFIT" ? (
                              <TrendingUp className="h-3 w-3 mr-1" />
                            ) : profitLoss.profitLossStatus === "LOSS" ? (
                              <TrendingDown className="h-3 w-3 mr-1" />
                            ) : (
                              <DollarSign className="h-3 w-3 mr-1" />
                            )}
                            {formatCurrency(profitLoss.estimatedProfit)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {formatPercentage(profitLoss.profitPercentage)}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Avg Cost:{" "}
                            {formatCurrency(profitLoss.avgCostPerShare)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            request?.status
                          )}`}
                        >
                          {request?.status || "N/A"}
                        </span>
                        <div>
                          <span
                            className={`text-sm font-medium ${getPriorityColor(
                              request?.priority
                            )}`}
                          >
                            {request?.priority || "N/A"}
                          </span>
                        </div>
                        {request?.flaggedForReview && (
                          <div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              Flagged
                            </span>
                          </div>
                        )}
                        {riskIndicators?.requiresAdminReview && (
                          <div>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Review
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openModal(request, "VIEW")}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {request?.status === "PENDING" && (
                          <>
                            <button
                              onClick={() => openModal(request, "APPROVED")}
                              className="text-green-600 hover:text-green-900"
                              title="Approve"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openModal(request, "REJECTED")}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.totalRecords > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing{" "}
                {((pagination?.current || 1) - 1) * (pagination?.limit || 10) +
                  1}{" "}
                to{" "}
                {Math.min(
                  (pagination?.current || 1) * (pagination?.limit || 10),
                  pagination?.totalRecords || 0
                )}{" "}
                of {pagination?.totalRecords || 0} results
              </div>
              {pagination?.total > 1 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() =>
                      handlePageChange((pagination?.current || 1) - 1)
                    }
                    disabled={!pagination?.hasPrev}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md">
                    {pagination?.current || 1}
                  </span>
                  <button
                    onClick={() =>
                      handlePageChange((pagination?.current || 1) + 1)
                    }
                    disabled={!pagination?.hasNext}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Modal with Purchase History */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">
                {actionType === "VIEW"
                  ? "Sell Request Details"
                  : actionType === "APPROVED"
                    ? "Approve Sell Request"
                    : "Reject Sell Request"}
              </h3>
            </div>

            <div className="px-6 py-4">
              {/* Request Basic Details */}
              <div className="mb-6">
                <h4 className="text-md font-semibold text-gray-900 mb-3">
                  Request Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedRequest?.user?.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedRequest?.user?.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedRequest?.product?.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedRequest?.product?.category || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Portfolio
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedRequest?.portfolio?.name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedRequest?.requestedQuantity || 0} units
                      {(selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED") &&
                        selectedRequest.adminResponse?.executedQuantity !== selectedRequest.requestedQuantity && (
                          <span className="block text-xs text-green-600 font-semibold">
                            Executed: {selectedRequest.adminResponse?.executedQuantity} units
                          </span>
                        )}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sell Price
                    </label>
                    <p className="text-sm font-semibold text-indigo-600">
                      {formatCurrency(selectedRequest?.pricePerShare)}
                      <span className="ml-1 text-[10px] text-gray-400 font-normal">(Requested)</span>
                    </p>
                    {(selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED") &&
                      selectedRequest.adminResponse?.executedPrice && (
                        <p className="text-sm font-bold text-green-600">
                          {formatCurrency(selectedRequest.adminResponse.executedPrice)}
                          <span className="ml-1 text-[10px] uppercase">(Executed)</span>
                        </p>
                      )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Buy Price
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatCurrency(selectedRequest?.product?.buyPrice || selectedRequest?.product?.currentPrice)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Sell Price
                    </label>
                    <p className="text-sm font-bold text-green-600">
                      {formatCurrency(selectedRequest?.product?.sellPrice || selectedRequest?.product?.currentPrice)}
                    </p>
                    {selectedRequest?.product?.sellPrice > 0 && selectedRequest?.pricePerShare !== selectedRequest?.product?.sellPrice && (
                      <p className={`text-[10px] font-medium ${selectedRequest.product.sellPrice > selectedRequest.pricePerShare ? 'text-green-500' : 'text-red-500'}`}>
                        {selectedRequest.product.sellPrice > selectedRequest.pricePerShare ? '↑ Improved' : '↓ Decreased'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Payout
                    </label>
                    <div className="flex flex-col">
                      <p className={`text-sm font-medium ${selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED" ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                        {formatCurrency(selectedRequest?.totalAmount)}
                      </p>
                      {(selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED") && (
                        <p className="text-sm font-bold text-indigo-600">
                          {formatCurrency(selectedRequest.adminResponse?.executedAmount || selectedRequest.totalAmount)}
                          <span className="ml-1 text-[10px] font-normal text-gray-500">(Final)</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase History */}
              {selectedRequest?.purchaseHistory && (
                <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2 text-blue-600" />
                    Purchase History
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Total Purchased
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedRequest.purchaseHistory
                          .totalPurchasedQuantity || 0}{" "}
                        units
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Available Now
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedRequest.purchaseHistory.availableQuantity || 0}{" "}
                        units
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Locked
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {selectedRequest.purchaseHistory.lockedQuantity || 0}{" "}
                        units
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Avg Purchase Price
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(
                          selectedRequest.purchaseHistory.avgPurchasePrice
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Total Invested
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(
                          selectedRequest.purchaseHistory.totalInvestedAmount
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Admin Negotiated Indicator */}
                  {selectedRequest.purchaseHistory.negotiatedPurchaseCount >
                    0 && (
                      <div className="mb-3 p-2 bg-purple-50 border border-purple-200 rounded flex items-center">
                        <Info className="h-4 w-4 text-purple-600 mr-2" />
                        <span className="text-xs text-purple-800">
                          {
                            selectedRequest.purchaseHistory
                              .negotiatedPurchaseCount
                          }{" "}
                          purchase(s) were admin-negotiated
                        </span>
                      </div>
                    )}

                  {/* Recent Purchases */}
                  {selectedRequest.purchaseHistory.recentPurchases &&
                    selectedRequest.purchaseHistory.recentPurchases.length >
                    0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Recent Purchases (Last 3)
                        </label>
                        <div className="space-y-2">
                          {selectedRequest.purchaseHistory.recentPurchases.map(
                            (purchase, idx) => {
                              const negotiation = purchase.negotiation;
                              return (
                                <div
                                  key={idx}
                                  className={`p-3 rounded border ${negotiation?.hadNegotiation
                                    ? "bg-purple-50 border-purple-200"
                                    : "bg-white border-gray-200"
                                    }`}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-500">
                                          {purchase.date
                                            ? new Date(
                                              purchase.date
                                            ).toLocaleDateString()
                                            : "N/A"}
                                        </span>
                                        {negotiation?.hadNegotiation && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                            Admin Negotiated
                                          </span>
                                        )}
                                      </div>
                                      {purchase.transactionId && (
                                        <div className="text-xs text-gray-400">
                                          ID: {purchase.transactionId}
                                        </div>
                                      )}
                                      <div className="text-sm text-gray-900 mt-1">
                                        <span className="font-medium">
                                          {purchase.quantity || 0} units
                                        </span>
                                        {" @ "}
                                        <span className="font-medium">
                                          {formatCurrency(
                                            purchase.pricePerShare
                                          )}
                                        </span>
                                        {" = "}
                                        <span className="font-semibold text-blue-600">
                                          {formatCurrency(purchase.totalAmount)}
                                        </span>
                                      </div>
                                      {negotiation?.hadNegotiation && (
                                        <>
                                          <div className="text-xs text-gray-600 mt-1">
                                            Original Price:{" "}
                                            {formatCurrency(
                                              negotiation.originalPrice
                                            )}
                                            {negotiation.priceDifference !==
                                              0 && (
                                                <>
                                                  {" → "}
                                                  <span
                                                    className={`${negotiation.priceDifference >
                                                      0
                                                      ? "text-red-600"
                                                      : "text-green-600"
                                                      } font-medium`}
                                                  >
                                                    {negotiation.priceDifference <
                                                      0
                                                      ? "Saved "
                                                      : "Extra Cost "}
                                                    {formatCurrency(
                                                      Math.abs(
                                                        negotiation.priceDifference *
                                                        purchase.quantity
                                                      )
                                                    )}
                                                    {negotiation.priceChangePercent !=
                                                      null &&
                                                      ` (${formatPercentage(
                                                        negotiation.priceChangePercent
                                                      )})`}
                                                  </span>
                                                </>
                                              )}
                                          </div>
                                          {negotiation.adminNotes && (
                                            <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 whitespace-pre-line">
                                              {negotiation.adminNotes}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* Profit/Loss Analysis */}
              {selectedRequest?.profitLossAnalysis && (
                <div className="mb-6 bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                    Profit/Loss Analysis
                  </h4>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Avg Cost/Share
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(
                          selectedRequest.profitLossAnalysis.avgCostPerShare
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED"
                          ? "Executed Price/Share"
                          : "Expected Sell Price"}
                      </label>
                      <p className={`text-sm font-semibold ${selectedRequest.adminResponse?.priceModified ? "text-green-600" : "text-gray-900"}`}>
                        {formatCurrency(
                          selectedRequest.profitLossAnalysis.sellPricePerShare
                        )}
                        {selectedRequest.adminResponse?.priceModified && (
                          <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1 rounded">Negotiated</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Cost Basis
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {formatCurrency(
                          selectedRequest.profitLossAnalysis.estimatedCostBasis
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {selectedRequest.status === "APPROVED" || selectedRequest.status === "COMPLETED"
                          ? "Final Payout"
                          : "Estimated Payout"}
                        {liveStats?.isModified && <span className="text-[8px] bg-blue-100 text-blue-600 px-1 rounded ml-1">Live</span>}
                      </label>
                      <p className={`text-sm font-semibold ${selectedRequest.adminResponse?.executedAmount ? "text-indigo-600" : "text-gray-900"}`}>
                        {formatCurrency(
                          liveStats ? liveStats.totalSellAmount : selectedRequest.profitLossAnalysis.totalSellAmount
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Estimated P/L {liveStats?.isModified && <span className="text-[8px] bg-blue-100 text-blue-600 px-1 rounded ml-1">Live</span>}
                      </label>
                      <p
                        className={`text-lg font-bold ${(liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis
                          .profitLossStatus) === "PROFIT"
                          ? "text-green-600"
                          : (liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis
                            .profitLossStatus) === "LOSS"
                            ? "text-red-600"
                            : "text-gray-600"
                          }`}
                      >
                        {formatCurrency(
                          liveStats ? liveStats.estimatedProfit : selectedRequest.profitLossAnalysis.estimatedProfit
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        P/L Percentage {liveStats?.isModified && <span className="text-[8px] bg-blue-100 text-blue-600 px-1 rounded ml-1">Live</span>}
                      </label>
                      <p
                        className={`text-lg font-bold ${(liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis
                          .profitLossStatus) === "PROFIT"
                          ? "text-green-600"
                          : (liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis
                            .profitLossStatus) === "LOSS"
                            ? "text-red-600"
                            : "text-gray-600"
                          }`}
                      >
                        {formatPercentage(
                          liveStats ? liveStats.profitPercentage : selectedRequest.profitLossAnalysis.profitPercentage
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mt-4 flex items-center justify-center">
                    <span
                      className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${getProfitLossColor(
                        liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis.profitLossStatus
                      )}`}
                    >
                      {(liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis.profitLossStatus) ===
                        "PROFIT" ? (
                        <CheckCircle className="h-5 w-5 mr-2" />
                      ) : (liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis
                        .profitLossStatus) === "LOSS" ? (
                        <AlertCircle className="h-5 w-5 mr-2" />
                      ) : (
                        <Info className="h-5 w-5 mr-2" />
                      )}
                      {liveStats ? liveStats.profitLossStatus : selectedRequest.profitLossAnalysis.profitLossStatus}
                      {liveStats?.isModified && <span className="ml-2 text-[10px] opacity-75 uppercase tracking-tighter">(Live P/L)</span>}
                    </span>
                  </div>

                  {/* Admin Negotiation Impact */}
                  {selectedRequest.profitLossAnalysis
                    .negotiationImpactSummary && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded">
                        <div className="flex items-start">
                          <Info className="h-4 w-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                          {typeof selectedRequest.profitLossAnalysis
                            .negotiationImpactSummary === "string" ? (
                            <span className="text-sm text-purple-800">
                              {
                                selectedRequest.profitLossAnalysis
                                  .negotiationImpactSummary
                              }
                            </span>
                          ) : (
                            <div className="text-sm text-purple-800 space-y-1">
                              <p className="font-semibold">
                                {
                                  selectedRequest.profitLossAnalysis
                                    .negotiationImpactSummary.description
                                }
                              </p>
                              {selectedRequest.profitLossAnalysis
                                .negotiationImpactSummary.impactOnThisSale && (
                                  <p>
                                    {
                                      selectedRequest.profitLossAnalysis
                                        .negotiationImpactSummary.impactOnThisSale
                                        .description
                                    }
                                  </p>
                                )}
                              <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-purple-100">
                                <div>
                                  <span className="font-medium text-purple-600">Avg. Negotiated Buy Price:</span>
                                  <p className="font-bold">{formatCurrency(selectedRequest.profitLossAnalysis.negotiationImpactSummary.avgNegotiatedPrice)}</p>
                                </div>
                                <div>
                                  <span className="font-medium text-purple-600">Net Impact on Cost:</span>
                                  <p className="font-bold">{formatCurrency(selectedRequest.profitLossAnalysis.negotiationImpactSummary.netNegotiationImpact)}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* Risk Indicators */}
              {selectedRequest?.riskIndicators && (
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                    Risk Assessment
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className={`p-3 rounded border ${selectedRequest.riskIndicators.sellingAtLoss
                        ? "bg-red-50 border-red-200"
                        : "bg-green-50 border-green-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Selling at Loss
                        </span>
                        {selectedRequest.riskIndicators.sellingAtLoss ? (
                          <X className="h-5 w-5 text-red-600" />
                        ) : (
                          <Check className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded border ${selectedRequest.riskIndicators.largeQuantitySale
                        ? "bg-yellow-50 border-yellow-200"
                        : "bg-green-50 border-green-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Large Quantity
                        </span>
                        {selectedRequest.riskIndicators.largeQuantitySale ? (
                          <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        ) : (
                          <Check className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded border ${selectedRequest.riskIndicators.hasNegotiatedShares
                        ? "bg-purple-50 border-purple-200"
                        : "bg-gray-50 border-gray-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Negotiated Shares
                        </span>
                        {selectedRequest.riskIndicators.hasNegotiatedShares ? (
                          <Info className="h-5 w-5 text-purple-600" />
                        ) : (
                          <Check className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <div
                      className={`p-3 rounded border ${selectedRequest.riskIndicators.requiresAdminReview
                        ? "bg-orange-50 border-orange-200"
                        : "bg-green-50 border-green-200"
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Requires Review
                        </span>
                        {selectedRequest.riskIndicators.requiresAdminReview ? (
                          <AlertTriangle className="h-5 w-5 text-orange-600" />
                        ) : (
                          <Check className="h-5 w-5 text-green-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sell Type
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest?.sellType || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Type
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest?.orderType || "N/A"}
                  </p>
                </div>
                {selectedRequest?.limitPrice && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Limit Price
                    </label>
                    <p className="text-sm text-gray-900">
                      {formatCurrency(selectedRequest.limitPrice)}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Risk Score
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest?.riskScore || 0}/100
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest?.priority || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      selectedRequest?.status
                    )}`}
                  >
                    {selectedRequest?.status || "N/A"}
                  </span>
                </div>
              </div>

              {/* Request Reason */}
              {selectedRequest?.requestReason && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request Reason
                  </label>
                  <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                    {selectedRequest.requestReason}
                  </p>
                </div>
              )}

              {/* Action Form */}
              {actionType !== "VIEW" && (
                <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
                  {actionType === "APPROVED" && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Executed Quantity *
                          </label>
                          <input
                            type="number"
                            value={actionData.executedQuantity}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              setActionData((prev) => ({
                                ...prev,
                                executedQuantity: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="1"
                            max={selectedRequest?.requestedQuantity || 1}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Max: {selectedRequest?.requestedQuantity || 0} units
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Executed Price *
                          </label>
                          <input
                            type="number"
                            value={actionData.executedPrice}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) =>
                              setActionData((prev) => ({
                                ...prev,
                                executedPrice: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                            step="0.01"
                          />
                          <p className="text-[10px] text-gray-500 mt-1 flex justify-between">
                            <span>Requested: {formatCurrency(selectedRequest?.pricePerShare)}</span>
                            {selectedRequest?.profitLossAnalysis?.negotiationImpactSummary?.avgNegotiatedPrice > 0 && (
                              <span className="text-purple-600 font-medium">Negotiated Buy: {formatCurrency(selectedRequest.profitLossAnalysis.negotiationImpactSummary.avgNegotiatedPrice)}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-800">
                          <strong>Total Payout:</strong>{" "}
                          {formatCurrency(
                            (parseFloat(actionData.executedPrice) || 0) *
                            (parseInt(actionData.executedQuantity) || 0)
                          )}
                        </p>
                      </div>
                      <div className="pt-4 border-t border-gray-100">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Select Payment Method *
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <button
                            type="button"
                            onClick={() =>
                              setActionData((prev) => ({
                                ...prev,
                                paymentMethod: "WALLET",
                              }))
                            }
                            className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all ${actionData.paymentMethod === "WALLET"
                              ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                              : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                          >
                            <Wallet className={`h-6 w-6 mb-2 ${actionData.paymentMethod === "WALLET" ? "text-blue-600" : "text-gray-400"}`} />
                            <span className={`text-sm font-medium ${actionData.paymentMethod === "WALLET" ? "text-blue-700" : "text-gray-600"}`}>
                              Add to User Wallet
                            </span>
                            <span className="text-xs text-gray-500 mt-1">Instant fund credit</span>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              setActionData((prev) => ({
                                ...prev,
                                paymentMethod: "BANK_TRANSFER",
                              }))
                            }
                            className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-all ${actionData.paymentMethod === "BANK_TRANSFER"
                              ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200"
                              : "border-gray-200 bg-white hover:border-gray-300"
                              }`}
                          >
                            <DollarSign className={`h-6 w-6 mb-2 ${actionData.paymentMethod === "BANK_TRANSFER" ? "text-orange-600" : "text-gray-400"}`} />
                            <span className={`text-sm font-medium ${actionData.paymentMethod === "BANK_TRANSFER" ? "text-orange-700" : "text-gray-600"}`}>
                              Manual Bank Transfer
                            </span>
                            <span className="text-xs text-gray-500 mt-1">Requires manual entry</span>
                          </button>
                        </div>
                      </div>

                      {actionData.paymentMethod === "BANK_TRANSFER" && (
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
                          <div>
                            <h5 className="text-sm font-bold text-orange-800 mb-2 flex items-center">
                              <Info className="h-4 w-4 mr-2" />
                              User's KYC Bank Details
                            </h5>
                            {selectedRequest?.user?.bankDetails ? (
                              <div className="grid grid-cols-2 gap-y-2 gap-x-4 bg-white p-3 rounded border border-orange-100 shadow-sm">
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Account Name</p>
                                  <p className="text-sm font-medium text-gray-900">{selectedRequest.user.bankDetails.accountName || "N/A"}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Bank Name</p>
                                  <p className="text-sm font-medium text-gray-900">{selectedRequest.user.bankDetails.bankName || "N/A"}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Account Number</p>
                                  <p className="text-sm font-mono font-bold text-gray-900">{selectedRequest.user.bankDetails.accountNumber || "N/A"}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">IFSC Code</p>
                                  <p className="text-sm font-mono font-bold text-gray-900">{selectedRequest.user.bankDetails.ifscCode || "N/A"}</p>
                                </div>
                              </div>
                            ) : (
                              <div className="p-3 bg-red-50 border border-red-100 rounded text-red-700 text-sm flex items-center">
                                <AlertTriangle className="h-4 w-4 mr-2" />
                                User has not provided bank details in KYC.
                              </div>
                            )}
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Transaction Reference Number *
                              </label>
                              <input
                                type="text"
                                placeholder="Enter reference number from your bank transfer"
                                value={actionData.transactionReference}
                                onChange={(e) =>
                                  setActionData((prev) => ({
                                    ...prev,
                                    transactionReference: e.target.value,
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                required
                              />
                            </div>

                            <label className="flex items-start space-x-3 cursor-pointer p-3 bg-white rounded border border-orange-100 hover:bg-orange-50 transition-colors">
                              <input
                                type="checkbox"
                                checked={actionData.isBankTransferConfirmed}
                                onChange={(e) =>
                                  setActionData((prev) => ({
                                    ...prev,
                                    isBankTransferConfirmed: e.target.checked,
                                  }))
                                }
                                className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                              />
                              <span className="text-sm text-gray-700 leading-tight">
                                I confirm that I have **manually transferred** the total payout amount of{" "}
                                <strong className="text-orange-700">
                                  {formatCurrency(
                                    (parseFloat(actionData.executedPrice) || 0) *
                                    (parseInt(actionData.executedQuantity) || 0)
                                  )}
                                </strong>{" "}
                                to the user's bank account mentioned above.
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {actionType === "REJECTED" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Rejection *
                      </label>
                      <textarea
                        value={actionData.reason}
                        onChange={(e) =>
                          setActionData((prev) => ({
                            ...prev,
                            reason: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Please provide a clear reason for rejection..."
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      value={actionData.notes}
                      onChange={(e) =>
                        setActionData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Any additional information or instructions..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 sticky bottom-0">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
              >
                {actionType === "VIEW" ? "Close" : "Cancel"}
              </button>
              {actionType !== "VIEW" && (
                <button
                  onClick={handleAction}
                  disabled={
                    processing ||
                    (actionType === "REJECTED" && !actionData.reason.trim()) ||
                    (actionType === "APPROVED" &&
                      (!actionData.executedPrice ||
                        !actionData.executedQuantity ||
                        (actionData.paymentMethod === "BANK_TRANSFER" &&
                          (!actionData.isBankTransferConfirmed || !actionData.transactionReference.trim()))))
                  }
                  className={`px-4 py-2 rounded-md text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${actionType === "APPROVED"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                    }`}
                >
                  {processing
                    ? "Processing..."
                    : actionType === "APPROVED"
                      ? "Approve & Execute"
                      : "Reject Request"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" />
    </div>
  );
};

export default ProductSell;
